"use client";

import "react-phone-number-input/style.css";

import type { Variants } from "framer-motion";
import { motion } from "framer-motion";
import PhoneInput from "react-phone-number-input";

import PhoneInputComponent from "@/components/shared/PhoneInputComponent";
import { useTranslation } from "@/hooks/useTranslation";
import type { BookingData } from "@/types";

type Props = {
  formData: BookingData;
  onInputChange?: (data: BookingData) => void;
};

export default function CustomerInfo({ formData, onInputChange }: Props) {
  const { t } = useTranslation();

  const itemVariants: Variants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.4,
        ease: "easeOut",
      },
    },
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={itemVariants}
      className="border-t p-5 lg:border-l lg:border-t-0"
    >
      <div className="flex flex-col justify-center gap-5">
        {/* name */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div className="flex flex-col gap-2">
            <label
              htmlFor="firstName"
              className={`block font-medium text-gray-700 `}
            >
              {t({ de: "Vorname", en: "First Name" })}{" "}
              <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              required
              placeholder={t({ de: "Vorname", en: "First Name" })}
              className={`w-full rounded-full border-2 border-primary-500 bg-primary-50 px-6 py-4 placeholder:text-primary-600 focus:outline-none focus:ring-2`}
              value={formData.customer.firstName}
              onChange={(
                e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
              ) => {
                const { value } = e.target;
                if (onInputChange) {
                  onInputChange({
                    ...formData,
                    customer: { ...formData.customer, firstName: value },
                  });
                }
              }}
            />
          </div>
          <div className="flex flex-col gap-2">
            <label
              htmlFor="surname"
              className={`block font-medium text-gray-700 `}
            >
              {t({ de: "Nachname", en: "Surname" })}{" "}
              <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="surname"
              name="surname"
              required
              placeholder={t({ de: "Nachname", en: "Surname" })}
              className={`w-full rounded-full border-2 border-primary-500 bg-primary-50 px-6 py-4 placeholder:text-primary-600 focus:outline-none focus:ring-2`}
              value={formData.customer.surname}
              onChange={(
                e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
              ) => {
                const { value } = e.target;
                if (onInputChange) {
                  onInputChange({
                    ...formData,
                    customer: { ...formData.customer, surname: value },
                  });
                }
              }}
            />
          </div>
        </div>

        {/* email & phone */}
        <div className="flex flex-col gap-2">
          <label htmlFor="email" className={`block font-medium text-gray-700 `}>
            {t({ de: "Email", en: "Email" })}{" "}
            <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            required
            placeholder={t({ de: "Email", en: "Email" })}
            className={`w-full rounded-full border-2 border-primary-500 bg-primary-50 px-6 py-4 placeholder:text-primary-600 focus:outline-none focus:ring-2`}
            value={formData.customer.email}
            onChange={(
              e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
            ) => {
              const { value } = e.target;
              if (onInputChange) {
                onInputChange({
                  ...formData,
                  customer: { ...formData.customer, email: value },
                });
              }
            }}
          />
        </div>
        <div className="flex flex-col gap-2">
          <label
            htmlFor="phoneNumber"
            className={`block font-medium text-gray-700 `}
          >
            {t({ de: "Telefonnummer", en: "Phone Number" })}{" "}
            <span className="text-red-500">*</span>
          </label>
          <PhoneInput
            type="tel"
            id="phoneNumber"
            name="phoneNumber"
            required
            placeholder={t({ de: "Telefonnummer", en: "Phone Number" })}
            className="rounded-full border-2 border-primary-500 bg-primary-50 pl-6"
            value={formData.customer.phoneNumber}
            defaultCountry="AT"
            inputComponent={PhoneInputComponent}
            onChange={(value) => {
              if (onInputChange) {
                onInputChange({
                  ...formData,
                  customer: {
                    ...formData.customer,
                    phoneNumber: value || "",
                  },
                });
              }
            }}
          />
        </div>
        {/* country  */}
        <div className="flex flex-col gap-2">
          <label
            htmlFor="country"
            className={`block font-medium text-gray-700 `}
          >
            {t({ de: "Land", en: "Country" })}{" "}
            <span className="text-red-500">*</span>
          </label>
          <input
            id="country"
            name="country"
            type="text"
            required
            className={`w-full rounded-full border-2 border-primary-500 bg-primary-50 px-6 py-4 placeholder:text-primary-600 focus:outline-none focus:ring-2`}
            placeholder={t({
              de: "Land",
              en: "Country",
            })}
            value={formData.customer.country}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const { value } = e.target;
              if (onInputChange) {
                onInputChange({
                  ...formData,
                  customer: { ...formData.customer, country: value },
                });
              }
            }}
          />
        </div>
      </div>
    </motion.div>
  );
}
